import { Link } from 'react-router';
import { useTranslation } from 'react-i18next';

import PhotoCarousel from './PhotoCarousel';
import { formatZAR } from '../lib/format';
import type { Listing } from '../types';

type Props = {
  listing: Listing;
  index: number;
};

export default function ListingCard({ listing, index }: Props) {
  const { t, i18n } = useTranslation();

  return (
    <Link
      to={`/stays/${listing.id}`}
      className="group relative block aspect-[4/5] overflow-hidden rounded-xs text-ink"
      aria-label={t('listings.card.aria', {
        name: t(`listings.${listing.id}.name`),
        area: t(`listings.${listing.id}.area`),
      })}
    >
      <PhotoCarousel
        photos={listing.photos}
        ratio="4/5"
        sizes="(min-width: 768px) 50vw, 100vw"
        index={index}
      />

      <div className="pointer-events-none absolute inset-0 bg-linear-to-b from-transparent from-30% to-black/55" />

      <div className="pointer-events-none absolute bottom-5 left-6 right-6 text-white">
        <div className="mb-1.5 text-xs uppercase tracking-[0.1em] opacity-85">
          {t(`listings.${listing.id}.area`)}
        </div>
        <div className="flex items-end justify-between gap-4">
          <h3 className="m-0 font-display text-[28px] font-semibold leading-tight tracking-display sm:text-[30px]">
            {t(`listings.${listing.id}.name`)}
          </h3>
          <div className="whitespace-nowrap text-sm">
            <span className="opacity-80">{t('listings.fromLabel')} </span>
            <strong className="font-semibold">{formatZAR(listing.price, i18n.language)}</strong>
            <span className="opacity-80"> {t('listings.perNight')}</span>
          </div>
        </div>
        <div className="mt-2.5 flex flex-wrap gap-3.5 text-[13px] opacity-85">
          {listing.rating != null && listing.reviews != null && (
            <>
              <span>★ {listing.rating.toFixed(2)} · {listing.reviews}</span>
              <span aria-hidden>·</span>
            </>
          )}
          <span>{t('listings.guestsCount', { count: listing.guests })}</span>
          <span aria-hidden>·</span>
          <span>{t('listings.bedsCount', { count: listing.beds })}</span>
        </div>
      </div>
    </Link>
  );
}
